import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Copy, Trash2, Pencil, Eye, PlusCircle, BarChart, Code } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { VideoCardUnified } from '@/components/shared/VideoCardUnified';
import { VideoGrid } from '@/components/shared/VideoGrid';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { useHookTrackStore } from '@/store/hook-track/hookTrackStore';
import { useAuth } from '@/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { t } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { EmptyHookTrackState } from '@/components/hook-track/EmptyHookTrackState';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export type HookTrackStatus = 'active' | 'finished' | 'draft';

const formatDate = (dateString: string | undefined | null) => {
  if (!dateString) return t('dateNotAvailable');

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return t('invalidDate');
    }
    return new Intl.DateTimeFormat(getInterfaceLanguage(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  } catch (error) {
    return t('invalidDate');
  }
};

const HookTrackList = () => {
  useLanguage();
  const [editingTrackId, setEditingTrackId] = useState<string | null>(null);
  const [copyCodeModalOpen, setCopyCodeModalOpen] = useState(false);
  const [selectedTrackForCode, setSelectedTrackForCode] = useState<{ id: string; name: string } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();

  const { tests, isLoading, error, fetchUserTests, updateTestTitle, deleteTest } = useHookTrackStore();

  // Buscar testes ao carregar a página
  useEffect(() => {
    const userId = user?.id_user || user?.id;
    if (userId) {
      fetchUserTests(userId);
    }
  }, [user?.id_user, user?.id, fetchUserTests]);

  // Buscar novamente quando navegarmos com state.refresh
  useEffect(() => {
    const state = location.state as { refresh?: boolean };
    if (state?.refresh) {
      const userId = user?.id_user || user?.id;
      if (userId) {
        fetchUserTests(userId);
        // Limpar o state para evitar refetch contínuo
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, user?.id_user, user?.id, fetchUserTests, navigate, location.pathname]);

  const handleCreateTest = () => {
    navigate('/hook-track/create');
  };

  const handleToggleStatus = (id: string) => {
    // TODO: Implementar alteração de status quando o backend suportar
    toast({
      title: t('statusUpdatedTitle'),
      description: t('statusUpdatedDesc'),
    });
  };

  const handleOpenRename = (trackId: string) => {
    setEditingTrackId(trackId);
  };

  const handleTitleChange = async (trackId: string, newTitle: string) => {
    if (!newTitle.trim()) return;

    try {
      await updateTestTitle(trackId, { leadTestTitle: newTitle.trim() });

      // Fechar modo de edição após sucesso
      setEditingTrackId(null);

      toast({
        title: t('testRenamedTitle'),
        description: t('testRenamedDesc'),
      });
    } catch (error) {
      toast({
        title: t('renameErrorTitle'),
        description: t('hookRenameTestError'),
        variant: "destructive",
      });
    }
  };

  const handleDuplicate = (id: string) => {
    // TODO: Implementar duplicação quando necessário
    toast({
      title: t('featureInDevelopment'),
      description: t('duplicateSoon'),
    });
  };

  const handleDelete = (id: string) => {
    setSelectedTest(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedTest) {
      try {
        await deleteTest(selectedTest);
        setDeleteDialogOpen(false);
        setSelectedTest(null);

        toast({
          title: t('testDeletedMsg'),
          description: t('hookTestDeletedDesc'),
        });
      } catch (error) {
        toast({
          title: t('deleteErrorTitle'),
          description: t('cannotDeleteTest'),
          variant: "destructive",
        });
      }
    }
  };

  // Loading skeleton
  if (isLoading && tests.length === 0) {
    return (
      <Layout fixedSidebar>
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-shrink-0 bg-transparent">
            <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 md:pt-4 pb-4 md:pb-5">
              <h1 className="text-[20px] md:text-[22px] font-semibold text-[#1E2330] tracking-tight leading-[1.2]">
                {t('hookTrackListTitle')}
              </h1>
            </div>
            <div className="hidden lg:block h-px w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }} />
          </div>
          <main className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              <VideoGrid>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-48 w-full rounded-lg" />
                ))}
              </VideoGrid>
            </div>
          </main>
        </div>
      </Layout>
    );
  }

  return (
    <Layout fixedSidebar>
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header Premium */}
        <div className="flex-shrink-0 bg-transparent">
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 md:pt-4 pb-4 md:pb-5">
            <h1 className="text-[20px] md:text-[22px] font-semibold text-[#1E2330] tracking-tight leading-[1.2]">
              {translateInterfaceText("Seus Testes de Introduções")}</h1>
          </div>
          <div className="hidden lg:block h-px w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }} />
        </div>
        <main className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6 animate-fade-in">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {tests.length === 0 ? (
              <EmptyHookTrackState onCreateNew={handleCreateTest} />
            ) : (
              <>
                {/* Botão de criar novo teste */}
                <div className="flex justify-end">
                  <Button onClick={handleCreateTest} className="flex items-center gap-2">
                    {t('createHookTest')}
                  </Button>
                </div>
                <VideoGrid>
                {/* Cards dos Testes Existentes */}
                {tests.map((track) => {
                  const trackId = track.id || track.id_lead_test || '';
                  const trackName = track.name || track.leadTestTitle || track.title || t('testWithoutName');
                  const trackCreatedAt = track.created_at || new Date().toISOString();
                  const isActive = track.is_active !== undefined ? track.is_active : track.status === 'active';
                  const trackThumbnail = track.originalVideoThumbnail || track.videos?.[0]?.thumbnail || 'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=400&h=225&auto=format&fit=crop';
                  const originalVideoId = track.originalVideoId || track.id_video || '';

                  return (
                    <VideoCardUnified
                      key={trackId}
                      id={trackId}
                      variant="list"
                      title={trackName}
                      thumbnail={trackThumbnail}
                      showToggle={true}
                      toggleValue={isActive}
                      onToggleChange={() => handleToggleStatus(trackId)}
                      footerDate={formatDate(trackCreatedAt)}
                      isEditing={editingTrackId === trackId}
                      onTitleChange={(newTitle) => handleTitleChange(trackId, newTitle)}
                      onEditingComplete={() => setEditingTrackId(null)}
                      actions={[
                        {
                          label: t('copyCode'),
                          icon: <Code className="h-4 w-4" />,
                          onClick: () => {
                            setSelectedTrackForCode({
                              id: originalVideoId,
                              name: trackName
                            });
                            setCopyCodeModalOpen(true);
                          },
                        },
                        {
                          label: t('rename'),
                          icon: <Pencil className="h-4 w-4" />,
                          onClick: () => handleOpenRename(trackId),
                        },
                        {
                          label: t('viewMetrics'),
                          icon: <BarChart className="h-4 w-4" />,
                          onClick: () => navigate(`/hook-track/${trackId}/metrics`),
                        },
                        {
                          label: t('delete'),
                          icon: <Trash2 className="h-4 w-4" />,
                          onClick: () => handleDelete(trackId),
                          variant: 'destructive' as const,
                        },
                      ]}
                    />
                  );
                })}
              </VideoGrid>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Copy Code Modal */}
      {selectedTrackForCode && (
        <CopyCodeModal
          isOpen={copyCodeModalOpen}
          onClose={() => {
            setCopyCodeModalOpen(false);
            setSelectedTrackForCode(null);
          }}
          entityType="hooktrack"
          entityId={selectedTrackForCode.id}
          entityName={selectedTrackForCode.name}
          userId={user?.id_user}
          videoOrigin="LEADTEST"
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="!w-[40vh] md:w-[35vh] rounded-xl p-6">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteHookTestTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteTestConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className='h-11 px-5 rounded-[10px] border-border text-muted-foreground hover:bg-muted hover:text-[#64748b] transition-colors'>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              {t('confirmDeleteTest')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default HookTrackList;
